import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { 
  Box, Typography, Button, Grid, Paper, CircularProgress, Alert,
  Card, CardContent, CardActions, Divider, List, ListItem, 
  ListItemIcon, ListItemText, IconButton, Dialog, DialogTitle, 
  DialogContent, DialogContentText, DialogActions, Switch, 
  FormControlLabel, Chip, TextField, InputAdornment, Menu, MenuItem
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  DeleteOutline as DeleteIcon,
  CheckCircle as CheckCircleIcon,
  AssessmentOutlined as AssessmentIcon,
  CancelOutlined as CancelIcon,
  MoreVert as MoreVertIcon,
  StarOutline as StarIcon,
  Star as StarFilledIcon,
  AddCircleOutline as AddCircleIcon,
  RemoveCircleOutline as RemoveCircleIcon
} from '@mui/icons-material';
import AdminSidebar from '../../components/Layout/AdminSidebar';

// Format currency helper function
const formatCurrency = (amount) => {
  if (amount === undefined || amount === null) return 'N/A';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2
  }).format(amount);
};

const MemberSubscriptionPlans = () => {
  const [memberPlans, setMemberPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [currentPlan, setCurrentPlan] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedPlanId, setSelectedPlanId] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);

  // Form state
  const [planForm, setPlanForm] = useState({
    name: '',
    description: '',
    price: '',
    interval: 'month',
    features: [''],
    isRecommended: false,
    active: true
  });

  // Fetch all member subscription plans on component mount
  useEffect(() => {
    const fetchMemberPlans = async () => {
      try {
        // Updated API endpoint to be explicit about fetching tenant-specific plans
        const res = await axios.get('/api/v1/member-subscription-plans/tenant');
        // Transform API data to match our enhanced format if needed
        const transformedPlans = res.data.data.map(plan => ({
          ...plan,
          features: plan.features || ['Basic membership'],
          interval: plan.interval || 'month',
          recommended: plan.isRecommended || false
        }));
        setMemberPlans(transformedPlans);
        setLoading(false);
      } catch (err) {
        const errorMessage = err.response?.data?.error || 'Error fetching member subscription plans';
        toast.error(errorMessage);
        setError(errorMessage);
        setLoading(false);
      }
    };

    fetchMemberPlans();
  }, []);

  // Handle opening dialog for new plan or editing
  const handleOpenDialog = (plan = null) => {
    if (plan) {
      setEditMode(true);
      setCurrentPlan(plan);
      setPlanForm({
        name: plan.name,
        description: plan.description || '',
        price: plan.price,
        interval: plan.interval || 'month',
        features: [...(plan.features || [''])],
        isRecommended: plan.recommended || false,
        active: plan.active !== false
      });
    } else {
      setEditMode(false);
      setCurrentPlan(null);
      setPlanForm({
        name: '',
        description: '',
        price: '',
        interval: 'month',
        features: [''],
        isRecommended: false,
        active: true
      });
    }
    setOpenDialog(true);
  };

  // Handle dialog close
  const handleCloseDialog = () => {
    setOpenDialog(false);
    setCurrentPlan(null);
  };

  // Handle form input change
  const handleInputChange = (e) => {
    const { name, value, checked, type } = e.target;
    setPlanForm({
      ...planForm,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  // Handle feature input change
  const handleFeatureChange = (index, value) => {
    const updatedFeatures = [...planForm.features];
    updatedFeatures[index] = value;
    setPlanForm({
      ...planForm,
      features: updatedFeatures
    });
  };

  // Add new feature input
  const addFeature = () => {
    setPlanForm({
      ...planForm,
      features: [...planForm.features, '']
    });
  };

  // Remove feature input
  const removeFeature = (index) => {
    const updatedFeatures = [...planForm.features];
    updatedFeatures.splice(index, 1);
    setPlanForm({
      ...planForm,
      features: updatedFeatures
    });
  };

  // Add new member subscription plan
  const handleSavePlan = async () => {
    try {
      // Validate form
      if (!planForm.name || !planForm.price) {
        setError("Please fill in all required fields");
        return;
      }
      
      // Filter out empty features
      const cleanedFeatures = planForm.features.filter(f => f.trim() !== '');
      if (cleanedFeatures.length === 0) {
        setError("Please add at least one feature");
        return;
      }
      
      // Format data for API - added tenant-specific flag
      const planData = {
        name: planForm.name,
        description: planForm.description,
        price: parseFloat(planForm.price),
        interval: planForm.interval,
        features: cleanedFeatures,
        isRecommended: planForm.isRecommended,
        active: planForm.active,
        isTenantSpecific: true // Mark this plan as specific to the current tenant
      };
      
      setLoading(true);
      
      if (editMode && currentPlan) {
        // Update existing plan
        const res = await axios.put(`/api/v1/member-subscription-plans/${currentPlan._id}`, planData);
        setMemberPlans(
          memberPlans.map((plan) =>
            plan._id === currentPlan._id ? res.data.data : plan
          )
        );
        setSuccess(`Plan "${planData.name}" updated successfully`);
      } else {
        // Create new plan
        const res = await axios.post('/api/v1/member-subscription-plans', planData);
        setMemberPlans([...memberPlans, res.data.data]);
        setSuccess(`Plan "${planData.name}" created successfully`);
      }
      
      setOpenDialog(false);
      setLoading(false);
      setTimeout(() => setSuccess(null), 5000);
      
    } catch (err) {
      const errorMsg = err.response?.data?.error || 'Error saving subscription plan';
      setError(errorMsg);
      toast.error(errorMsg);
      setLoading(false);
    }
  };

  // Delete member subscription plan
  const handleDeleteConfirm = (plan) => {
    setCurrentPlan(plan);
    setDeleteConfirmOpen(true);
  };

  const handleDeletePlan = async () => {
    try {
      await axios.delete(`/api/v1/member-subscription-plans/${currentPlan._id}`);
      setMemberPlans(memberPlans.filter((plan) => plan._id !== currentPlan._id));
      setDeleteConfirmOpen(false);
      setSuccess(`Plan "${currentPlan.name}" deleted successfully`);
      toast.success('Member subscription plan deleted');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      const errorMsg = err.response?.data?.error || 'Error deleting subscription plan';
      setError(errorMsg);
      toast.error(errorMsg);
    }
  };

  // Handle plan menu open
  const handlePlanMenuOpen = (event, planId) => {
    setAnchorEl(event.currentTarget);
    setSelectedPlanId(planId);
  };

  // Handle plan menu close
  const handlePlanMenuClose = () => {
    setAnchorEl(null);
    setSelectedPlanId(null);
  };

  // Find selected plan
  const getSelectedPlan = () => {
    return memberPlans.find(plan => plan._id === selectedPlanId);
  };

  // Handle setting a plan as recommended
  const handleSetRecommended = async (planId) => {
    try {
      const updatedPlans = memberPlans.map(plan => {
        const isRecommended = plan._id === planId;
        if (plan.recommended !== isRecommended) {
          // We'll update this plan
          axios.put(`/api/v1/member-subscription-plans/${plan._id}`, {
            ...plan,
            isRecommended: isRecommended
          });
        }
        return {
          ...plan,
          recommended: isRecommended
        };
      });
      
      setMemberPlans(updatedPlans);
      setSuccess("Recommended plan updated successfully");
      setTimeout(() => setSuccess(null), 3000);
      handlePlanMenuClose();
      
    } catch (err) {
      const errorMsg = err.response?.data?.error || 'Error updating recommended plan';
      setError(errorMsg);
      toast.error(errorMsg);
    }
  };

  if (loading && memberPlans.length === 0) {
    return (
      <AdminSidebar>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
          <CircularProgress />
        </Box>
      </AdminSidebar>
    );
  }

  return (
    <AdminSidebar>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          bgcolor: '#f5f7fa',
        }}
      >
        {/* Header - updated title and description to be more specific */}
        <Paper 
          elevation={0} 
          sx={{ 
            mb: 4, 
            p: 3, 
            borderRadius: 2,
            background: 'linear-gradient(135deg, #3f51b5 0%, #5c6bc0 100%)',
            color: 'white'
          }}
        >
          <Typography variant="h4" sx={{ mb: 1, fontWeight: 'medium', display: 'flex', alignItems: 'center' }}>
            <AssessmentIcon sx={{ mr: 1 }} /> Tenant Membership Plans
          </Typography>
          <Typography variant="body1" sx={{ opacity: 0.8 }}>
            Create and manage subscription plans specific to your members
          </Typography>
        </Paper>

        {/* Action Bar */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h5">Available Plans</Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
            sx={{
              background: 'linear-gradient(45deg, #3f51b5 30%, #5c6bc0 90%)',
              boxShadow: '0 4px 6px rgba(63, 81, 181, 0.25)',
            }}
          >
            Add New Plan
          </Button>
        </Box>

        {/* Alert Messages */}
        {error && (
          <Alert 
            severity="error" 
            sx={{ mb: 3 }}
            onClose={() => setError(null)}
          >
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert 
            severity="success" 
            sx={{ mb: 3 }}
            onClose={() => setSuccess(null)}
          >
            {success}
          </Alert>
        )}

        {/* Plans Grid */}
        <Grid container spacing={3}>
          {memberPlans.length > 0 ? (
            memberPlans.map((plan) => (
              <Grid item xs={12} md={4} key={plan._id}>
                <Card 
                  elevation={plan.recommended ? 3 : 1} 
                  sx={{ 
                    height: '100%',
                    position: 'relative',
                    overflow: 'visible',
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: 4
                    },
                    border: plan.recommended ? '2px solid #3f51b5' : 'none',
                  }}
                >
                  {plan.recommended && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: -12,
                        left: 0,
                        right: 0,
                        textAlign: 'center',
                        zIndex: 1
                      }}
                    >
                      <Chip
                        label="Recommended"
                        color="primary"
                        size="small"
                        icon={<StarFilledIcon />}
                      />
                    </Box>
                  )}

                  {/* Plan Actions Menu */}
                  <IconButton
                    aria-label="more"
                    aria-controls="plan-menu"
                    aria-haspopup="true"
                    onClick={(e) => handlePlanMenuOpen(e, plan._id)}
                    sx={{ position: 'absolute', top: 8, right: 8 }}
                  >
                    <MoreVertIcon />
                  </IconButton>

                  <CardContent sx={{ pt: plan.recommended ? 4 : 2 }}>
                    <Typography variant="h5" component="h2" gutterBottom>
                      {plan.name}
                    </Typography>
                    
                    <Typography 
                      variant="h4" 
                      component="div" 
                      color="primary"
                      sx={{ display: 'flex', alignItems: 'baseline', mb: 2 }}
                    >
                      {formatCurrency(plan.price)}
                      <Typography variant="body2" component="span" color="text.secondary" sx={{ ml: 1 }}>
                        /{plan.interval || 'month'}
                      </Typography>
                    </Typography>

                    <Typography 
                      variant="body2" 
                      color="text.secondary"
                      sx={{ mb: 3, minHeight: 60 }}
                    >
                      {plan.description || 'Basic membership subscription plan'}
                    </Typography>

                    <Divider sx={{ my: 2 }} />
                    
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                      Features:
                    </Typography>
                    
                    <List dense disablePadding>
                      {(plan.features || ['Basic membership features']).map((feature, index) => (
                        <ListItem key={index} disableGutters sx={{ py: 0.5 }}>
                          <ListItemIcon sx={{ minWidth: 36 }}>
                            <CheckCircleIcon color="primary" fontSize="small" />
                          </ListItemIcon>
                          <ListItemText primary={feature} />
                        </ListItem>
                      ))}
                    </List>
                  </CardContent>
                  
                  <CardActions sx={{ p: 2, pt: 0, justifyContent: 'flex-end' }}>
                    <IconButton 
                      size="small" 
                      color="primary" 
                      onClick={() => handleOpenDialog(plan)}
                      title="Edit plan"
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    
                    <IconButton 
                      size="small" 
                      color="error"
                      onClick={() => handleDeleteConfirm(plan)}
                      title="Delete plan"
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </CardActions>
                </Card>
              </Grid>
            ))
          ) : (
            <Grid item xs={12}>
              <Alert severity="info">
                No subscription plans found. Add one to get started.
              </Alert>
            </Grid>
          )}
        </Grid>

        {/* Plan Form Dialog */}
        <Dialog
          open={openDialog}
          onClose={handleCloseDialog}
          fullWidth
          maxWidth="md"
        >
          <DialogTitle>
            {editMode ? `Edit ${currentPlan?.name} Plan` : 'Create New Plan'}
          </DialogTitle>
          <DialogContent sx={{ pt: 2 }}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Plan Name"
                  name="name"
                  value={planForm.name}
                  onChange={handleInputChange}
                  fullWidth
                  margin="normal"
                  variant="outlined"
                  required
                />
                
                <TextField
                  label="Description"
                  name="description"
                  value={planForm.description}
                  onChange={handleInputChange}
                  fullWidth
                  margin="normal"
                  variant="outlined"
                  multiline
                  rows={3}
                  required
                />
                
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <TextField
                    label="Price"
                    name="price"
                    value={planForm.price}
                    onChange={handleInputChange}
                    type="number"
                    inputProps={{ min: 0, step: "0.01" }}
                    fullWidth
                    margin="normal"
                    variant="outlined"
                    required
                    InputProps={{
                      startAdornment: <InputAdornment position="start">$</InputAdornment>,
                    }}
                  />
                  
                  <TextField
                    label="Billing Interval"
                    name="interval"
                    value={planForm.interval}
                    onChange={handleInputChange}
                    select
                    SelectProps={{ native: true }}
                    fullWidth
                    margin="normal"
                    variant="outlined"
                    required
                  >
                    <option value="month">Monthly</option>
                    <option value="year">Yearly</option>
                  </TextField>
                </Box>
                
                <Box sx={{ mt: 2 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={planForm.isRecommended}
                        onChange={handleInputChange}
                        name="isRecommended"
                        color="primary"
                      />
                    }
                    label="Mark as Recommended Plan"
                  />
                  
                  <FormControlLabel
                    control={
                      <Switch
                        checked={planForm.active}
                        onChange={handleInputChange}
                        name="active"
                        color="primary"
                      />
                    }
                    label="Plan is Active"
                  />
                </Box>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="subtitle1">Plan Features</Typography>
                  <Button
                    startIcon={<AddCircleIcon />}
                    onClick={addFeature}
                    size="small"
                  >
                    Add Feature
                  </Button>
                </Box>
                
                {planForm.features.map((feature, index) => (
                  <Box
                    key={index}
                    sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      mb: 1,
                      gap: 1
                    }}
                  >
                    <CheckCircleIcon color="primary" fontSize="small" />
                    <TextField
                      value={feature}
                      onChange={(e) => handleFeatureChange(index, e.target.value)}
                      fullWidth
                      variant="outlined"
                      size="small"
                      placeholder={`Feature ${index + 1}`}
                    />
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => removeFeature(index)}
                      disabled={planForm.features.length <= 1}
                    >
                      <RemoveCircleIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 3 }}>
            <Button onClick={handleCloseDialog} color="inherit">
              Cancel
            </Button>
            <Button
              onClick={handleSavePlan}
              variant="contained"
              color="primary"
              startIcon={editMode ? <EditIcon /> : <AddIcon />}
              disabled={loading}
            >
              {loading ? (
                <CircularProgress size={24} />
              ) : (
                editMode ? 'Update Plan' : 'Create Plan'
              )}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={deleteConfirmOpen}
          onClose={() => setDeleteConfirmOpen(false)}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
        >
          <DialogTitle id="alert-dialog-title">
            Delete Plan?
          </DialogTitle>
          <DialogContent>
            <DialogContentText id="alert-dialog-description">
              Are you sure you want to delete the "{currentPlan?.name}" plan? This action cannot be undone.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteConfirmOpen(false)} color="inherit">
              Cancel
            </Button>
            <Button
              onClick={handleDeletePlan}
              color="error"
              variant="contained"
              autoFocus
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>

        {/* Plan Actions Menu */}
        <Menu
          id="plan-menu"
          anchorEl={anchorEl}
          keepMounted
          open={Boolean(anchorEl)}
          onClose={handlePlanMenuClose}
        >
          <MenuItem onClick={() => {
            handleOpenDialog(getSelectedPlan());
            handlePlanMenuClose();
          }}>
            <ListItemIcon>
              <EditIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Edit Plan" />
          </MenuItem>
          <MenuItem onClick={() => handleSetRecommended(selectedPlanId)}>
            <ListItemIcon>
              <StarIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Set as Recommended" />
          </MenuItem>
          <MenuItem onClick={() => {
            handleDeleteConfirm(getSelectedPlan());
            handlePlanMenuClose();
          }}>
            <ListItemIcon>
              <DeleteIcon fontSize="small" color="error" />
            </ListItemIcon>
            <ListItemText primary="Delete Plan" />
          </MenuItem>
        </Menu>
      </Box>
    </AdminSidebar>
  );
};

export default MemberSubscriptionPlans;
